/**
 * 🤖 PRONTUÁRIO ALERTAS DE IA - NÍVEL 2
 *
 * Alertas individuais por paciente, exibidos no topo do prontuário.
 *
 * Regras:
 * 1. MELD Vencido (Portaria 8041, Art. 133 §4º)
 * 2. Imagem Vencida - CHC (Portaria 8041, Art. 139)
 * 3. CSPH Presumida (AASLD 2024 / Baveno VII)
 * 4. Sem Consulta Recente
 * 5. MELD < 11 com inscrição ativa (suspensão obrigatória)
 * 6. Soro vencido (>90 dias sem soro atualizado)
 */

import { useMemo, useState } from "react";
import React from "react";
import {
  AlertTriangle,
  Microscope,
  Calendar,
  Hospital,
  Bot,
  Tag,
} from "lucide-react";
import { useExames } from "../../exames/context/ExamesContext";
import { useExamesImagem } from "../../exames-imagem/context/ExamesImagemContext";
import { useEvolucao } from "../../evolucao/context/EvolucaoContext";
import { theme } from "../../../shared/styles/theme";
import { Paciente } from "../../../types";
import { textoMencionaVarizesPresentes } from "../../../shared/utils/detectarVarizes";
import {
  achadoEndoscopiaPresente,
  lerLinhaEstruturada,
} from "../../ia-analise/utils/extrairAchadosIA";
import { ehRespostaPositiva } from "../../../shared/utils/respostaNegativa";

interface Props {
  paciente: Paciente;
}

interface AlertaProntuario {
  id: string;
  tipo: "critico" | "alerta" | "atencao" | "info";
  icone: string | React.ElementType;
  titulo: string;
  detalhe: string;
  fundamentacao: string;
  acao?: string; // Sugestão de conduta
}

// ═══════════════════════════════════════════════════════
// FUNÇÕES UTILITÁRIAS
// ═══════════════════════════════════════════════════════

function getMELDValidade(meldScore: number): { dias: number; label: string } {
  if (meldScore > 25) return { dias: 7, label: "7 dias" };
  if (meldScore >= 19) return { dias: 30, label: "1 mês" };
  if (meldScore >= 11) return { dias: 90, label: "3 meses" };
  return { dias: 365, label: "12 meses" };
}

function formatarData(dataStr: string): string {
  if (!dataStr) return "N/A";
  const dataCorrigida = dataStr.includes("T") ? dataStr : `${dataStr}T12:00:00`;
  const data = new Date(dataCorrigida);
  if (isNaN(data.getTime())) return "N/A";
  return data.toLocaleDateString("pt-BR");
}

function diasDesde(dataStr: string): number {
  if (!dataStr) return 999;
  const dataCorrigida = dataStr.includes("T") ? dataStr : `${dataStr}T12:00:00`;
  const data = new Date(dataCorrigida);
  if (isNaN(data.getTime())) return 999;
  return Math.floor(
    (new Date().getTime() - data.getTime()) / (1000 * 60 * 60 * 24)
  );
}

// ═══════════════════════════════════════════════════════
// COMPONENTE
// ═══════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════
// ACESSORES COM FALLBACK (inscricaoTx legado → campos planos)
// ═══════════════════════════════════════════════════════
function getNumeroRGCT(p: Paciente): string | undefined {
  return (p.inscricaoTx as any)?.numeroRGCT || p.numeroRGCT || p.rgct;
}
function getDataTransplante(p: Paciente): string | undefined {
  return (p.inscricaoTx as any)?.dataTransplante || p.dataTransplante;
}
function getSituacaoEspecial(p: Paciente): boolean {
  return (
    (p.inscricaoTx as any)?.situacaoEspecial === true ||
    p.situacaoEspecial === true
  );
}
function getTipoSE(p: Paciente): string {
  return (p.inscricaoTx as any)?.tipoSE || p.tipoSE || "";
}

export default function ProntuarioAlertasIA({ paciente }: Props) {
  const { exames } = useExames();
  const { exames: examesImagem } = useExamesImagem();
  const { evolucoes } = useEvolucao();
  const [collapsed, setCollapsed] = useState(false);

  const alertas = useMemo(() => {
    const lista: AlertaProntuario[] = [];

    // Paciente inativo — não mostrar alertas
    if (paciente.statusPaciente && paciente.statusPaciente !== "ativo") {
      return lista;
    }

    const isFigado = paciente.tipoAtendimento?.toLowerCase() === "figado";
    const isInscritoLista =
      isFigado && getNumeroRGCT(paciente) && !getDataTransplante(paciente);
    const isPosTx = !!getDataTransplante(paciente);
    const temSE = getSituacaoEspecial(paciente);

    // Exames deste paciente
    const examesPac = exames
      .filter((e) => e.pacienteId === paciente.id)
      .sort((a, b) => new Date(b.data).getTime() - new Date(a.data).getTime());

    const imagensPac = examesImagem
      .filter((e) => e.pacienteId === paciente.id)
      .sort(
        (a, b) =>
          new Date(b.dataExame).getTime() - new Date(a.dataExame).getTime()
      );

    // B323 Bloco 3 (AI_146): consulta cancelada é registro anulado — não conta
    // como "última consulta" nem alimenta/suprime alerta clínico.
    const evolucoesPac = evolucoes
      .filter((e) => e.pacienteId === paciente.id && e.cancelado !== true)
      .sort((a, b) => new Date(b.data).getTime() - new Date(a.data).getTime());

    // ══════════════════════════════════════════════
    // 1. MELD VENCIDO (só inscritos em lista)
    // ══════════════════════════════════════════════
    if (isInscritoLista) {
      const exameComMeld = examesPac.find((e) => e.meld && e.meld > 0);

      if (!exameComMeld) {
        lista.push({
          id: "meld-sem-registro",
          tipo: "critico",
          icone: "🔴",
          titulo: "Sem MELD registrado",
          detalhe: "Paciente inscrito em lista sem MELD calculado no sistema.",
          fundamentacao: "Portaria 8041/2025, Art. 133",
          acao: "Solicitar exames: creatinina, bilirrubina total, INR, sódio e albumina (mesma coleta).",
        });
      } else {
        const meldScore = exameComMeld.meld!;
        const validade = getMELDValidade(meldScore);
        const dias = diasDesde(exameComMeld.data);
        const diasRestantes = validade.dias - dias;

        if (diasRestantes < 0) {
          lista.push({
            id: "meld-vencido",
            tipo: "critico",
            icone: "🔴",
            titulo: `MELD ${meldScore} — Vencido há ${Math.abs(
              diasRestantes
            )} dia(s)`,
            detalhe: `Exames coletados em ${formatarData(
              exameComMeld.data
            )}. Validade para MELD ${meldScore}: ${validade.label}.`,
            fundamentacao: "Portaria 8041/2025, Art. 133, §4º",
            acao: "Coletar novos exames MELD imediatamente: creatinina, bilirrubina total, INR, sódio e albumina.",
          });
        } else if (diasRestantes <= 7) {
          lista.push({
            id: "meld-vencendo",
            tipo: "alerta",
            icone: "🟡",
            titulo: `MELD ${meldScore} — Vence em ${diasRestantes} dia(s)`,
            detalhe: `Exames coletados em ${formatarData(
              exameComMeld.data
            )}. Validade: ${validade.label}.`,
            fundamentacao: "Portaria 8041/2025, Art. 133, §4º",
            acao: "Agendar coleta de exames MELD antes do vencimento.",
          });
        }

        // MELD < 11 — suspensão automática pela Central
        if (meldScore < 11 && !temSE) {
          lista.push({
            id: "meld-baixo",
            tipo: "atencao",
            icone: AlertTriangle,
            titulo: `MELD ${meldScore} — Abaixo do mínimo para lista`,
            detalhe:
              "MELD < 11 sem situação especial: paciente será suspenso automaticamente pelo sistema da Central.",
            fundamentacao: "Portaria 8041/2025, Art. 86",
            acao: "Para reativar: aguardar MELD ≥ 11 em nova coleta, enviar solicitação específica a central ou obter aprovação de Situação Especial.",
          });
        }
      }
    }

    // ══════════════════════════════════════════════
    // 2. IMAGEM VENCIDA — SITUAÇÃO ESPECIAL
    //    Portaria 8041/2025, Art. 139-145
    // ══════════════════════════════════════════════
    if (isInscritoLista && temSE) {
      const tipoSE = getTipoSE(paciente);
      const prazo = 180;

      const exigenciasPorSE: Record<
        string,
        { descricao: string; tipos: string[][] }
      > = {
        "Carcinoma Hepatocelular (CHC)": {
          descricao: "TC/RM abdome + TC tórax",
          tipos: [
            [
              "TC Abdome",
              "TC Abdome e Tórax",
              "RM Abdome",
              "Tomografia",
              "Ressonância",
            ],
            ["TC Tórax", "TC Abdome e Tórax", "Tomografia"],
          ],
        },
        "Carcinoma Hepatocelular por Downstaging": {
          descricao: "TC/RM abdome trifásica + TC tórax",
          tipos: [
            [
              "TC Abdome",
              "TC Abdome e Tórax",
              "RM Abdome",
              "Tomografia",
              "Ressonância",
            ],
            ["TC Tórax", "TC Abdome e Tórax", "Tomografia"],
          ],
        },
        "Tumor Neuroendócrino Metastático": {
          descricao: "TC tórax + TC abdome + mapeamento ósseo",
          tipos: [
            ["TC Abdome", "TC Abdome e Tórax", "Tomografia"],
            ["TC Tórax", "TC Abdome e Tórax", "Tomografia"],
            ["Mapeamento Ósseo", "Cintilografia"],
          ],
        },
        Hepatoblastoma: {
          descricao: "TC tórax + TC abdome + mapeamento ósseo",
          tipos: [
            ["TC Abdome", "TC Abdome e Tórax", "Tomografia"],
            ["TC Tórax", "TC Abdome e Tórax", "Tomografia"],
            ["Mapeamento Ósseo", "Cintilografia"],
          ],
        },
        "Carcinoma Fibrolamelar Irressecável": {
          descricao: "TC tórax + TC abdome",
          tipos: [
            ["TC Abdome", "TC Abdome e Tórax", "Tomografia"],
            ["TC Tórax", "TC Abdome e Tórax", "Tomografia"],
          ],
        },
        "Hemangioendotelioma Epitelióide Primário de Fígado Irressecável": {
          descricao: "TC ou RM abdome",
          tipos: [
            [
              "TC Abdome",
              "TC Abdome e Tórax",
              "RM Abdome",
              "Tomografia",
              "Ressonância",
            ],
          ],
        },
        "Adenomatose Múltipla Irressecável": {
          descricao: "TC ou RM abdome",
          tipos: [
            [
              "TC Abdome",
              "TC Abdome e Tórax",
              "RM Abdome",
              "Tomografia",
              "Ressonância",
            ],
          ],
        },
        "Síndrome Hepatopulmonar": {
          descricao: "TC tórax + espirometria",
          tipos: [
            ["TC Tórax", "TC Abdome e Tórax", "Tomografia"],
            ["Espirometria", "Prova Função Pulmonar"],
          ],
        },
      };

      const exigencia = exigenciasPorSE[tipoSE];

      if (exigencia) {
        const examesVencidos: string[] = [];
        const examesAVencer: string[] = [];

        exigencia.tipos.forEach((grupoTipos) => {
          const ultimoDoGrupo = imagensPac.find((e) =>
            grupoTipos.includes(e.tipoExame)
          );
          if (!ultimoDoGrupo) {
            examesVencidos.push(`${grupoTipos[0]} não registrado`);
          } else {
            const dias = diasDesde(ultimoDoGrupo.dataExame);
            if (dias > prazo) {
              examesVencidos.push(
                `${ultimoDoGrupo.tipoExame}: ${formatarData(
                  ultimoDoGrupo.dataExame
                )} (${dias} dias atrás)`
              );
            } else if (dias > 150) {
              examesAVencer.push(
                `${ultimoDoGrupo.tipoExame} vence em ${prazo - dias} dias`
              );
            }
          }
        });

        if (examesVencidos.length > 0) {
          lista.push({
            id: "imagem-se-vencida",
            tipo: "critico",
            icone: "🔴",
            titulo: `SE (${tipoSE}): Imagem vencida`,
            detalhe: `Exige: ${
              exigencia.descricao
            } · Pendente: ${examesVencidos.join(" · ")}`,
            fundamentacao: "Portaria 8041/2025, Art. 139-145",
            acao: `Solicitar renovação: ${exigencia.descricao}`,
          });
        } else if (examesAVencer.length > 0) {
          lista.push({
            id: "imagem-se-vencendo",
            tipo: "alerta",
            icone: "🟡",
            titulo: `SE (${tipoSE}): Imagem próxima do vencimento`,
            detalhe: examesAVencer.join(" · "),
            fundamentacao: "Portaria 8041/2025, Art. 139-145",
            acao: `Agendar renovação: ${exigencia.descricao}`,
          });
        }
      }
    }

    // ══════════════════════════════════════════════
    // 3. CSPH PRESUMIDA (AASLD 2024)
    // ══════════════════════════════════════════════
    if (isFigado && !isPosTx) {
      const motivosCSPH: string[] = [];

      // Achados da IA
      for (const img of imagensPac) {
        if (!img.achados) continue;

        if (img.achados.ascite === "SIM") {
          motivosCSPH.push(
            `Ascite (${img.tipoExame || "Exame"} ${formatarData(
              img.dataExame
            )})`
          );
        }

        // B361 (AI_156): fonte única da negação (antes: lista local duplicada aqui).
        const shNeg = !ehRespostaPositiva(img.achados.shunts);
        if (img.achados.shunts && !shNeg) {
          motivosCSPH.push(`Shunts: ${img.achados.shunts}`);
        }

        const tpVal = img.achados.trombosePortal?.toLowerCase().trim() || "";
        const tpNeg =
          !tpVal ||
          tpVal.startsWith("não") ||
          tpVal.startsWith("nao") ||
          tpVal.startsWith("ausent") ||
          tpVal.startsWith("sem ");
        if (img.achados.trombosePortal && !tpNeg) {
          motivosCSPH.push(`Trombose portal: ${img.achados.trombosePortal}`);
        }

        // Colaterais portossistêmicas (varizes tratadas à parte, abaixo, com
        // detecção negação-consciente sobre laudo + resumo + achados).
        if (img.achados.outrosAchados) {
          const termos = [
            "recanalização",
            "recanalizacao",
            "paraumbilical",
            "esplenorrenal",
            "hepatofugal",
            "colateral",
          ];
          const lower = img.achados.outrosAchados.toLowerCase();
          for (const t of termos) {
            if (lower.includes(t)) {
              motivosCSPH.push(`${t} (achados IA)`);
              break;
            }
          }
        }
      }

      // ── VARIZES = CSPH definitiva (Baveno VII) ──────────────────
      // B301 (AI_137): detecção em 3 camadas, da mais PRECISA à mais tolerante:
      //  1) campo ESTRUTURADO da EDA (achados.varizesEsofagicas/Gastricas) — o
      //     parser passou a guardá-lo; vocabulário controlado (F1/GOV1/NÃO...).
      //  2) PONTE p/ EDAs analisadas ANTES do B301: lê a linha VARIZES_*: do bloco
      //     ACHADOS_EXAME embutido no texto da análise — precisa, sem reanálise.
      //  3) FALLBACK negação-consciente sobre laudo digitado à mão / resumo /
      //     conclusão (NÃO o texto integral da análise, p/ não confundir "rastreio
      //     de varizes" de uma EDA NEGATIVA com presença — falso-positivo do B300).
      for (const img of imagensPac) {
        const varizesEstruturada =
          achadoEndoscopiaPresente(img.achados?.varizesEsofagicas) ||
          achadoEndoscopiaPresente(img.achados?.varizesGastricas);

        const semCampoEstruturado =
          !img.achados?.varizesEsofagicas && !img.achados?.varizesGastricas;
        const textoBloco = `${img.analiseCompleta || ""}\n${
          img.laudo || ""
        }\n${img.resumoAchados || ""}`;
        const varizesPonte =
          semCampoEstruturado &&
          (achadoEndoscopiaPresente(
            lerLinhaEstruturada(textoBloco, "VARIZES_ESOFAGICAS")
          ) ||
            achadoEndoscopiaPresente(
              lerLinhaEstruturada(textoBloco, "VARIZES_GASTRICAS")
            ));

        const textoLaudo = [
          img.laudo || "",
          img.resumoAchados || "",
          img.achados?.outrosAchados || "",
          img.achados?.conclusao || "",
          img.achados?.conclusaoLaudistaLiteral || "",
        ].join(". ");
        const varizesTexto = textoMencionaVarizesPresentes(textoLaudo);

        if (varizesEstruturada || varizesPonte || varizesTexto) {
          const ehEndoscopia =
            img.tipoExame === "Endoscopia" || img.tipoExame === "Ecoendoscopia";
          const rotulo = ehEndoscopia ? "Varizes na EDA" : "Varizes";
          motivosCSPH.push(
            `${rotulo} (${img.tipoExame} ${formatarData(img.dataExame)})`
          );
        }
      }

      // Elastografia
      const elastografias = imagensPac.filter(
        (e) => e.tipoExame === "Elastografia"
      );
      const plaquetas = examesPac.find(
        (e) => e.plaquetas && e.plaquetas > 0
      )?.plaquetas;

      // B301 cont. (AI_138): ler a rigidez do CAMPO ESTRUTURADO
      // (achados.rigidezKpa) quando disponível — preciso; senão, PONTE por regex
      // do laudo/análise (elastografias pré-estruturação ou laudo digitado à mão).
      const elasto = elastografias[0];
      let lsm: number | null = null;
      if (elasto) {
        const estrut = elasto.achados?.rigidezKpa;
        const mEstrut = estrut ? estrut.match(/(\d+[.,]?\d*)/) : null;
        if (mEstrut) {
          lsm = parseFloat(mEstrut[1].replace(",", "."));
        } else {
          const textoElasto = `${elasto.laudo || ""}\n${
            elasto.analiseCompleta || ""
          }\n${elasto.resumoAchados || ""}`;
          const mTexto = textoElasto.match(/(\d+[.,]?\d*)\s*kPa/i);
          if (mTexto) lsm = parseFloat(mTexto[1].replace(",", "."));
        }
      }
      if (lsm !== null && !Number.isNaN(lsm)) {
        if (lsm > 25) {
          motivosCSPH.push(`Elastografia: ${lsm} kPa (>25)`);
        } else if (lsm >= 20 && plaquetas && plaquetas < 150000) {
          motivosCSPH.push(
            `Elastografia: ${lsm} kPa + Plt ${(plaquetas / 1000).toFixed(0)}K`
          );
        } else if (lsm >= 15 && plaquetas && plaquetas < 110000) {
          motivosCSPH.push(
            `Elastografia: ${lsm} kPa + Plt ${(plaquetas / 1000).toFixed(0)}K`
          );
        }
      }

      const unicos = motivosCSPH.filter((m, i) => motivosCSPH.indexOf(m) === i);

      // Verificar se já está em NSBB (carvedilol/propranolol/nadolol)
      // Busca em TODAS as consultas e em TODOS os campos de texto
      const termosNSBB = ["carvedilol", "propranolol", "nadolol"];
      const medicacoesCad = (paciente.medicacoesEmUso || "").toLowerCase();
      const evolucoesPacCSPH = evolucoes.filter(
        (e: any) => e.pacienteId === paciente.id && e.cancelado !== true
      );
      const textoEvo = evolucoesPacCSPH
        .map((e: any) =>
          [
            e.queixaPrincipal || "",
            e.historiaDoencaAtual || "",
            e.revisaoSistemas || "",
            e.exameFisico || "",
            e.conduta || "",
            e.evolucaoLivre || "", // B90: campo único (retorno/pós-Tx/intercorrência)
          ].join(" ")
        )
        .join(" ")
        .toLowerCase();
      const jaEmNSBB = termosNSBB.some(
        (t) => medicacoesCad.includes(t) || textoEvo.includes(t)
      );

      if (unicos.length > 0 && !jaEmNSBB) {
        lista.push({
          id: "csph-presumida",
          tipo: "info",
          icone: Microscope,
          titulo: "CSPH Presumida",
          detalhe: `Achados: ${unicos.slice(0, 3).join(", ")}${
            unicos.length > 3 ? ` (+${unicos.length - 3})` : ""
          }`,
          fundamentacao: "AASLD Practice Guidance 2024 / Baveno VII",
          acao: "Considerar Carvedilol 6.25-12.5mg/dia para prevenir descompensação. Avaliar EDA se não realizada.",
        });
      }
    }

    // ══════════════════════════════════════════════
    // 4. EDA VENCIDA (AASLD 2024 / Baveno VII)
    //    - Sem EDA prévia: alerta imediato
    //    - Com varizes: prazo 2 anos
    //    - Sem varizes (EDA negativa): prazo 3 anos
    // ══════════════════════════════════════════════
    if (isFigado && !isPosTx && evolucoesPac.length > 0) {
      const prazoEdaPadrao = 730; // 2 anos — doença ativa ou sem laudo
      const prazoEdaNegativa = 1095; // 3 anos — última EDA sem varizes (Baveno VII)

      const termosVarizesEda = [
        "variz",
        "varizes",
        "varicosa",
        "varicosas",
        "varicosidade",
        "cordões varicosos",
        "cordoes varicosos",
        "ectasia vascular",
      ];

      const endoscopiasPac = imagensPac.filter(
        (e) => e.tipoExame === "Endoscopia"
      );

      if (endoscopiasPac.length === 0) {
        lista.push({
          id: "eda-ausente",
          tipo: "atencao",
          icone: AlertTriangle,
          titulo: "EDA não registrada",
          detalhe:
            "Nenhuma endoscopia digestiva alta registrada no sistema. Rastreamento de varizes esofágicas pendente.",
          fundamentacao: "Baveno VII / AASLD 2024",
          acao: "Solicitar EDA para rastreamento de varizes esofágicas.",
        });
      } else {
        const ultimaEda = endoscopiasPac[0]; // já ordenado por dataExame desc
        const laudoTexto = (ultimaEda.laudo || "").toLowerCase();
        const resumoTexto = (ultimaEda.resumoAchados || "").toLowerCase();
        const textoCompleto = `${laudoTexto} ${resumoTexto}`.trim();

        // ════════════════════════════════════════════════════════
        // CASO 1: EDA sem laudo digitado nem análise de IA.
        // Sem informação textual, o sistema não pode inferir
        // presença de varizes nem aplicar prazo de seguimento.
        // Mostra alerta específico em vez de "adivinhar".
        // ════════════════════════════════════════════════════════
        if (textoCompleto.length === 0) {
          lista.push({
            id: "eda-sem-analise",
            tipo: "atencao",
            icone: AlertTriangle,
            titulo: "EDA sem análise — rastreamento indeterminado",
            detalhe: `Última EDA: ${formatarData(
              ultimaEda.dataExame
            )}. Laudo não digitado e sem análise de IA — não é possível inferir presença de varizes nem aplicar prazo de seguimento.`,
            fundamentacao: "Baveno VII / AASLD 2024",
            acao: "Rodar análise de IA no exame OU digitar o laudo manualmente para liberar o rastreamento automatizado.",
          });
        } else {
          // ════════════════════════════════════════════════════════
          // CASO 2: tem texto → segue lógica de vigilância normal.
          // EDA sem varizes → 3 anos (Baveno VII)
          // EDA com varizes → 2 anos
          // ════════════════════════════════════════════════════════
          const temVarizes = termosVarizesEda.some((t) =>
            textoCompleto.includes(t)
          );

          const prazo = temVarizes ? prazoEdaPadrao : prazoEdaNegativa;

          const diasUltimaEda = diasDesde(ultimaEda.dataExame);

          if (diasUltimaEda > prazo) {
            const mesesAtras = Math.floor(diasUltimaEda / 30);
            lista.push({
              id: "eda-vencida",
              tipo: "atencao",
              icone: AlertTriangle,
              titulo: temVarizes
                ? `EDA Vencida — Varizes documentadas (há ${mesesAtras} meses)`
                : `EDA Vencida — Sem varizes (há ${mesesAtras} meses)`,
              detalhe: temVarizes
                ? `Última EDA: ${formatarData(
                    ultimaEda.dataExame
                  )} com varizes documentadas. Prazo máximo: 2 anos.`
                : `Última EDA: ${formatarData(
                    ultimaEda.dataExame
                  )} sem varizes. Prazo máximo: 3 anos (Baveno VII).`,
              fundamentacao: "Baveno VII / AASLD 2024",
              acao: temVarizes
                ? "Repetir EDA — varizes documentadas exigem vigilância bianual."
                : "Repetir EDA — prazo trienal para EDA negativa expirado.",
            });
          }
        }
      }
    }
    // ══════════════════════════════════════════════
    // 5. SEM CONSULTA RECENTE
    // ══════════════════════════════════════════════
    if (evolucoesPac.length === 0) {
      const diasCadastro = paciente.dataCadastro
        ? diasDesde(paciente.dataCadastro)
        : 0;
      if (diasCadastro > 30) {
        lista.push({
          id: "sem-consulta",
          tipo: "atencao",
          icone: Calendar,
          titulo: "Nenhuma consulta registrada",
          detalhe: `Paciente cadastrado há ${diasCadastro} dias sem consulta no sistema.`,
          fundamentacao: "Acompanhamento ambulatorial regular",
          acao: "Registrar evolução clínica ou verificar se paciente compareceu a consulta.",
        });
      }
    } else {
      const diasUltimaConsulta = diasDesde(evolucoesPac[0].data);
      if (diasUltimaConsulta > 365) {
        lista.push({
          id: "sem-consulta-1ano",
          tipo: "alerta",
          icone: Calendar,
          titulo: `Sem consulta há ${Math.floor(
            diasUltimaConsulta / 30
          )} meses`,
          detalhe: `Última consulta: ${formatarData(
            evolucoesPac[0].data
          )}. Avaliar perda de seguimento.`,
          fundamentacao: "Acompanhamento ambulatorial regular",
          acao: "Contactar paciente. Considerar alterar status para 'Perda de Seguimento'.",
        });
      } else if (diasUltimaConsulta > 180) {
        lista.push({
          id: "sem-consulta-6m",
          tipo: "atencao",
          icone: Calendar,
          titulo: `Sem consulta há ${Math.floor(
            diasUltimaConsulta / 30
          )} meses`,
          detalhe: `Última consulta: ${formatarData(evolucoesPac[0].data)}.`,
          fundamentacao: "Acompanhamento ambulatorial regular",
          acao: "Agendar retorno para acompanhamento.",
        });
      }
    }

    // ══════════════════════════════════════════════
    // 6. PÓS-TRANSPLANTE: Tempo de Tx
    // ══════════════════════════════════════════════
    if (isPosTx && getDataTransplante(paciente)) {
      const diasTx = diasDesde(getDataTransplante(paciente)!);

      // Primeiro ano pós-Tx: acompanhamento intensivo
      if (diasTx <= 90) {
        lista.push({
          id: "pos-tx-precoce",
          tipo: "info",
          icone: Hospital,
          titulo: `Pós-Transplante Precoce (${diasTx} dias)`,
          detalhe:
            "Período crítico: atenção para rejeição, infecções e função do enxerto.",
          fundamentacao: "Protocolo pós-transplante hepático",
          acao: "Monitorar tacrolimus, função hepática e renal. Vigilância para CMV e infecções.",
        });
      }
    }

    return lista;
  }, [paciente, exames, examesImagem, evolucoes]);

  // ═══════════════════════════════════════════════
  // Se não tem alertas, não renderizar nada
  // ═══════════════════════════════════════════════
  if (alertas.length === 0) return null;

  const cores = {
    critico: { bg: "#fef2f2", border: "#ef4444", text: "#991b1b", icon: "🔴" },
    alerta: { bg: "#fffbeb", border: "#f59e0b", text: "#92400e", icon: "🟡" },
    atencao: { bg: "#fff7ed", border: "#f97316", text: "#9a3412", icon: "🟠" },
    info: { bg: "#eff6ff", border: "#3b82f6", text: "#1e3a8a", icon: "🔵" },
  };

  const temCritico = alertas.some((a) => a.tipo === "critico");

  return (
    <div
      style={{
        margin: "12px 16px 0",
        borderRadius: theme.borderRadius.lg,
        border: `1px solid ${temCritico ? "#fca5a5" : "#e2e8f0"}`,
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div
        style={{
          background: temCritico
            ? "linear-gradient(135deg, #991b1b 0%, #b91c1c 100%)"
            : "linear-gradient(135deg, #1e40af 0%, #2563eb 100%)",
          padding: "10px 16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
        }}
        onClick={() => setCollapsed(!collapsed)}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            color: "white",
          }}
        >
          <Bot size={18} />
          <span
            style={{
              fontSize: "13px",
              fontWeight: "700",
              fontFamily: theme.fonts.sans,
            }}
          >
            Insights de IA
          </span>
          <span
            style={{
              background: "rgba(255,255,255,0.25)",
              padding: "2px 10px",
              borderRadius: theme.borderRadius.full,
              fontSize: "11px",
              fontWeight: "700",
              fontFamily: theme.fonts.sans,
            }}
          >
            {alertas.length} alerta{alertas.length > 1 ? "s" : ""}
          </span>
        </div>
        <span
          style={{
            color: "white",
            fontSize: "14px",
            transition: "transform 0.2s",
            transform: collapsed ? "rotate(0deg)" : "rotate(180deg)",
          }}
        >
          ▼
        </span>
      </div>

      {/* Alertas */}
      {!collapsed && (
        <div
          style={{
            background: "#f8fafc",
            padding: "8px",
            display: "flex",
            flexDirection: "column",
            gap: "6px",
          }}
        >
          {alertas.map((alerta) => {
            const cor = cores[alerta.tipo];
            return (
              <div
                key={alerta.id}
                style={{
                  background: cor.bg,
                  border: `1px solid ${cor.border}`,
                  borderLeft: `4px solid ${cor.border}`,
                  borderRadius: theme.borderRadius.md,
                  padding: "10px 12px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "8px",
                  }}
                >
                  <span style={{ fontSize: "16px", flexShrink: 0 }}>
                    {typeof alerta.icone === "string"
                      ? alerta.icone
                      : React.createElement(alerta.icone as React.ElementType, {
                          size: 16,
                        })}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: "13px",
                        fontWeight: "700",
                        color: cor.text,
                        fontFamily: theme.fonts.sans,
                      }}
                    >
                      {alerta.titulo}
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: cor.text,
                        opacity: 0.85,
                        marginTop: "2px",
                        fontFamily: theme.fonts.sans,
                      }}
                    >
                      {alerta.detalhe}
                    </div>
                    {alerta.acao && (
                      <div
                        style={{
                          fontSize: "11px",
                          color: cor.text,
                          marginTop: "4px",
                          fontWeight: "600",
                          fontFamily: theme.fonts.sans,
                          display: "flex",
                          alignItems: "flex-start",
                          gap: "4px",
                        }}
                      >
                        <Tag size={12} />
                        <span>{alerta.acao}</span>
                      </div>
                    )}
                    <div
                      style={{
                        fontSize: "10px",
                        color: cor.text,
                        opacity: 0.5,
                        marginTop: "4px",
                        fontStyle: "italic",
                        fontFamily: theme.fonts.sans,
                      }}
                    >
                      {alerta.fundamentacao}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
